/**
 * Body Stats Service
 * Manual and OCR entries use the same Firestore collection. Most recent value wins.
 */

import {
  getCollection,
  addDocument,
  deleteDocument,
} from './firebase/firestore.service';
import { where, orderBy } from 'firebase/firestore';
import type { BodyStatsEntry } from '../types/bodyStats';
import { getTodayDate, canEditDate } from '../utils/date';
import { saveBodyMetrics } from './tdee.service';
import { triggerFaRecomputeInBackground } from './functionalAge.service';

const BODY_STATS_ENTRIES_COLLECTION = 'bodyStatsEntries';

/**
 * Save a body stats entry (manual or OCR)
 */
export async function saveBodyStatsEntry(
  userId: string,
  entry: Omit<BodyStatsEntry, 'id' | 'userId' | 'date' | 'timestamp' | 'createdAt' | 'updatedAt'>,
  date?: string,
): Promise<BodyStatsEntry> {
  if (typeof window !== 'undefined') {
    const { isDemoMode } = await import('../demo');
    if (isDemoMode()) {
      throw new Error('Demo mode: read-only. Cannot save entries.');
    }
  }
  
  const entryDate = date || getTodayDate();
  if (!canEditDate(entryDate)) {
    throw new Error(`Cannot edit entries for ${entryDate}. Days auto-close at 23:59.`);
  }
  
  const now = Date.now();
  
  // Firestore doesn't accept undefined values — filter them out
  const cleanEntry: Record<string, unknown> = {
    userId,
    date: entryDate,
    timestamp: now,
    createdAt: now,
    updatedAt: now,
  };
  
  const entryKeys: Array<keyof typeof entry> = [
    'source',
    'entry_type',
    'weight',
    'bodyFat',
    'muscleMass',
    'boneMass',
    'subcutaneousFat',
    'visceralFat',
    'waistCm',
    'bloodPressure',
    'restingHR',
    'imageUrl',
    'ocrConfidence',
    'ocrNotes',
  ];
  
  entryKeys.forEach((key) => {
    const value = entry[key];
    if (value !== undefined && value !== null) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        const cleanNested: Record<string, unknown> = {};
        Object.keys(value).forEach((nestedKey) => {
          const nestedValue = (value as Record<string, unknown>)[nestedKey];
          if (nestedValue !== undefined && nestedValue !== null) {
            cleanNested[nestedKey] = nestedValue;
          }
        });
        if (Object.keys(cleanNested).length > 0) {
          cleanEntry[key] = cleanNested;
        }
      } else {
        cleanEntry[key] = value;
      }
    }
  });
  
  const saved = await addDocument<Omit<BodyStatsEntry, 'id'>>(
    BODY_STATS_ENTRIES_COLLECTION,
    cleanEntry as Omit<BodyStatsEntry, 'id'>,
  );
  
  // Update bodyMetrics with only the fields being saved (no read-before-write needed).
  // saveBodyMetrics will overwrite only the fields we send; existing fields are preserved.
  const hasAnyMetric = saved.weight || saved.bodyFat !== undefined || saved.muscleMass !== undefined ||
    saved.boneMass !== undefined || saved.subcutaneousFat !== undefined || saved.visceralFat !== undefined ||
    saved.waistCm !== undefined || saved.restingHR !== undefined || saved.bloodPressure !== undefined;
    
  if (hasAnyMetric) {
    try {
      const metricsUpdate: Record<string, unknown> = {};
      if (saved.weight) metricsUpdate.currentWeight = saved.weight;
      if (saved.bodyFat !== undefined) metricsUpdate.currentBodyFat = saved.bodyFat;
      if (saved.muscleMass !== undefined) metricsUpdate.muscleMass = saved.muscleMass;
      if (saved.boneMass !== undefined) metricsUpdate.boneMass = saved.boneMass;
      if (saved.subcutaneousFat !== undefined) metricsUpdate.subcutaneousFat = saved.subcutaneousFat;
      if (saved.visceralFat !== undefined) metricsUpdate.visceralFat = saved.visceralFat;
      if (saved.waistCm !== undefined) metricsUpdate.waistCm = saved.waistCm;
      if (saved.restingHR !== undefined) metricsUpdate.restingHR = saved.restingHR;
      if (saved.bloodPressure !== undefined) metricsUpdate.bloodPressure = saved.bloodPressure;

      await saveBodyMetrics(userId, metricsUpdate as any);
    } catch (error) {
      console.error('Failed to update body metrics from body stats entry:', error);
    }
  }
  
  // Fire-and-forget: trigger FA recompute in background after body stats save
  triggerFaRecomputeInBackground(userId, 'event:bodyStats');
  
  return saved;
}

/**
 * Get body stats entries for a date
 */
export async function getBodyStatsEntriesForDate(
  userId: string,
  date: string,
): Promise<BodyStatsEntry[]> {
  return getCollection<BodyStatsEntry>(
    BODY_STATS_ENTRIES_COLLECTION,
    where('userId', '==', userId),
    where('date', '==', date),
    orderBy('timestamp', 'desc'),
  );
}

/**
 * Get latest body stats entry (most recent)
 */
export async function getLatestBodyStatsEntry(
  userId: string,
): Promise<BodyStatsEntry | null> {
  const entries = await getCollection<BodyStatsEntry>(
    BODY_STATS_ENTRIES_COLLECTION,
    where('userId', '==', userId),
    orderBy('timestamp', 'desc'),
  );
  return entries.length > 0 ? entries[0] : null;
}

/**
 * Get body stats entries for a date range
 */
export async function getBodyStatsEntriesForDateRange(
  userId: string,
  startDate: string,
  endDate: string,
): Promise<BodyStatsEntry[]> {
  const { getDocumentsByUserAndDateRange } = await import('./firebase/firestore.service');
  return getDocumentsByUserAndDateRange<BodyStatsEntry>(
    BODY_STATS_ENTRIES_COLLECTION,
    userId,
    startDate,
    endDate,
  );
}

/**
 * Delete a body stats entry
 */
export async function deleteBodyStatsEntry(entryId: string): Promise<void> {
  const { isDemoMode } = await import('../demo');
  if (isDemoMode()) {
    throw new Error('Demo mode: read-only. Cannot delete entries.');
  }
  await deleteDocument(`${BODY_STATS_ENTRIES_COLLECTION}/${entryId}`);
}